
import { normalizeError } from "@/hooks/useErrorHandler";

/**
 * Security-focused error handler that prevents information leakage
 */

// Development mode check
const isDevelopment =
  (import.meta as any).env?.MODE === 'development' || process.env.NODE_ENV === 'development';

// Generic error messages for production
const GENERIC_ERRORS = {
  network: 'Errore di connessione. Riprova più tardi.',
  validation: 'Dati non validi. Controlla i campi inseriti.',
  authentication: 'Sessione scaduta. Effettua nuovamente l\'accesso.',
  authorization: 'Non hai i permessi per questa operazione.',
  server: 'Errore del server. Riprova più tardi.',
  default: 'Si è verificato un errore. Riprova più tardi.'
};

const NETWORK_ERROR_PATTERNS = ['network', 'fetch', 'offline'];

const isNetworkMessage = (message: string): boolean => {
  const lowerCased = message.toLowerCase();
  return (
    lowerCased === GENERIC_ERRORS.network.toLowerCase() ||
    NETWORK_ERROR_PATTERNS.some(pattern => lowerCased.includes(pattern))
  );
};

/**
 * Utility helper to identify network/offline error messages
 */
export function isNetworkErrorMessage(error: unknown): boolean {
  const normalized = normalizeError(error);
  return isNetworkMessage(normalized.message);
}

/**
 * True when `message` is one of the generic, user-facing strings produced by
 * sanitizeErrorMessage. These must NEVER be used as the diagnostic message sent
 * to Sentry/log_errors: distinct causes would collapse into a single,
 * unactionable bucket (Sentry CERALIA-90). When a generic string reaches the
 * logger, the original error should be used to derive the real message instead.
 */
export function isGenericUserMessage(message: unknown): boolean {
  return typeof message === 'string' && Object.values(GENERIC_ERRORS).includes(message);
}

/**
 * Sanitize error message for user display
 */
export function sanitizeErrorMessage(error: unknown): string {
  const normalized = normalizeError(error);

  // In development, show detailed errors
  if (isDevelopment) {
    return normalized.message;
  }

  // In production, use generic messages based on error type
  if (isNetworkMessage(normalized.message)) {
    return GENERIC_ERRORS.network;
  }

  const message = normalized.message.toLowerCase();

  if (message.includes('invalid') || message.includes('validation')) {
    return GENERIC_ERRORS.validation;
  }

  if (message.includes('password') && message.includes('contain')) {
    return 'Password non sufficientemente complessa. Deve contenere almeno 8 caratteri, una maiuscola, una minuscola, un numero e un simbolo.';
  }
  
  if (message.includes('unauthorized') || message.includes('token')) {
    return GENERIC_ERRORS.authentication;
  }
  
  if (message.includes('forbidden') || message.includes('permission')) {
    return GENERIC_ERRORS.authorization;
  }
  
  if (message.includes('server') || message.includes('500')) {
    return GENERIC_ERRORS.server;
  }
  
  return GENERIC_ERRORS.default;
}

/**
 * Log error securely (remove sensitive data)
 */
export function logErrorSecurely(error: unknown, context?: string): void {
  const normalized = normalizeError(error);
  
  // Remove sensitive information from error details
  const sanitizedDetails = sanitizeErrorDetails(normalized.details);
  
  const logEntry = {
    timestamp: new Date().toISOString(),
    context: context || 'unknown',
    message: normalized.message,
    code: normalized.code,
    status: normalized.status,
    details: sanitizedDetails
  };
  
  // In development, log everything
  if (isDevelopment) {
    console.error('[SECURE_ERROR]', logEntry);
    return;
  }
  
  // In production, only log non-sensitive information
  console.error('[ERROR]', {
    timestamp: logEntry.timestamp,
    context: logEntry.context,
    code: logEntry.code,
    status: logEntry.status
  });
}

/**
 * Remove sensitive information from error details
 */
function sanitizeErrorDetails(details: unknown): unknown {
  if (!details) return null;
  
  if (typeof details === 'string') {
    return details
      .replace(/password[=:]\s*[^\s,}]+/gi, 'password=***')
      .replace(/token[=:]\s*[^\s,}]+/gi, 'token=***')
      .replace(/key[=:]\s*[^\s,}]+/gi, 'key=***')
      .replace(/secret[=:]\s*[^\s,}]+/gi, 'secret=***');
  }
  
  if (typeof details === 'object') {
    try {
      const str = JSON.stringify(details);
      const sanitized = str
        .replace(/("password"\s*:\s*")[^"]*"/gi, '$1***"')
        .replace(/("token"\s*:\s*")[^"]*"/gi, '$1***"')
        .replace(/("key"\s*:\s*")[^"]*"/gi, '$1***"')
        .replace(/("secret"\s*:\s*")[^"]*"/gi, '$1***"');
      
      return JSON.parse(sanitized);
    } catch {
      return '[OBJECT_SANITIZATION_FAILED]';
    }
  }
  
  return details;
}

/**
 * Handle rate limiting errors
 */
export function handleRateLimitError(remainingTime: number): string {
  const minutes = Math.ceil(remainingTime / 60000);
  return `Troppi tentativi. Riprova tra ${minutes} minuto${minutes > 1 ? 'i' : ''}.`;
}
